import React, { useEffect } from 'react'
import { ControlGroup, Intent, Switch, MenuItem } from '@blueprintjs/core'
import { Button } from '@ui'
import SimpleColorPicker from './SimpleColorPicker'
import { colors } from './colors'
import { Select2 } from '@blueprintjs/select'
import Preview from './Preview/Preview'
import { QrCodeFileFormat } from '@api/QRCode/download'
import css from './qrContent.module.scss'

const sizeOptions: { value: string; title: string }[] = [
  { value: 'small', title: 'Small' },
  { value: 'medium', title: 'Medium' },
  { value: 'large', title: 'Large' },
]

const Selector = Select2<{ value: string; title: string }>

export default function QrImageContent({
  size,
  color,
  type,
  fileFormat,
  isTransparent,
  isProcessing,
  onSizeChange,
  onFileFormatChange,
  onChangeColor,
  onTransparentChange,
  onDownload,
  onCancel,
}: {
  size: { value: string; title: string }
  type: string
  color: string
  fileFormat: string
  isTransparent: boolean
  isProcessing?: boolean
  onFileFormatChange: (format: QrCodeFileFormat) => void
  onSizeChange: (size: { value: string; title: string }) => void
  onChangeColor: (color: string) => void
  onTransparentChange: () => void
  onDownload: () => void
  onCancel: () => void
}) {
  useEffect(() => {
    if (type === 'image') {
      onSizeChange?.(sizeOptions[0])
      onFileFormatChange?.('PNG' as QrCodeFileFormat)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  return (
    <React.Fragment>
      <p className={`${css.title} text-grey-1000-70`}>
        Generate the QR Codes in bulk and download a zip folder of all QR Code images.
      </p>
      <div className="flex mb-5">
        <div className={css.col} style={{ flexBasis: '40%' }}>
          <section className={css.section}>
            <label className={`${css.format_label} text-grey-1000-70`}>Format</label>
            <ControlGroup>
              <Button
                blue
                style={{ borderRadius: '4px 0 0 4px', width: '100px', height: '32px' }}
                onClick={() => onFileFormatChange('PNG')}
                intent={fileFormat === 'PNG' ? Intent.PRIMARY : Intent.NONE}
                text="PNG"
              />
              <Button
                blue
                style={{ borderRadius: '0 0px 0px 0', width: '100px', height: '32px' }}
                onClick={() => onFileFormatChange('JPG')}
                intent={fileFormat === 'JPG' ? Intent.PRIMARY : Intent.NONE}
                text="JPG"
              />
              <Button
                blue
                style={{ borderRadius: '0 4px 4px 0', width: '103px', height: '32px' }}
                onClick={() => onFileFormatChange('PDF')}
                intent={fileFormat === 'PDF' ? Intent.PRIMARY : Intent.NONE}
                text="PDF"
              />
            </ControlGroup>
          </section>
          <section className={css.section}>
            <label className={css.label}>
              <span className="text-grey-1000">Select QR Code color</span>
              <SimpleColorPicker
                colors={colors}
                onChange={onChangeColor}
                value={color}
                buttonStyle={{ padding: 6 }}
                containerStyle={{ marginRight: 10 }}
              />
            </label>
          </section>
          {fileFormat === 'PNG' && (
            <section className={css.section}>
              <label className={css.label} onClick={onTransparentChange}>
                <span className="text-grey-1000">Make background transparent</span>
                <Switch
                  large
                  style={{ marginBottom: 0 }}
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={isTransparent}
                  onChange={onTransparentChange}
                />
              </label>
              {isTransparent && (
                <span style={{ fontSize: '12px' }} className="text-grey-1000-70">
                  Warning: The PNG file format supports transparent backgrounds and can be printed
                  on a transparent film factsheet.
                </span>
              )}
            </section>
          )}
        </div>
        <div className={css.col} style={{ flexBasis: '55%' }}>
          <section className={css.section}>
            <label className={`${css.format_label} text-grey-1000-70`}>Size</label>
            <Selector
              items={sizeOptions}
              popoverProps={{ minimal: true }}
              itemRenderer={(i, { handleClick }) => (
                <MenuItem
                  key={i.value}
                  style={{ width: 290 }}
                  text={i.title}
                  onClick={handleClick}
                />
              )}
              filterable={false}
              onItemSelect={(i) => onSizeChange?.(i)}>
              <Button
                text={size.title}
                style={{ width: 300, justifyContent: 'space-between' }}
                rightIconName="chevron-down"
              />
            </Selector>
          </section>
          <section className={css.section}>
            <Preview color={color} transparent={isTransparent} />
          </section>
        </div>
      </div>
      <section className={`${css.section} flex justify-end`} style={{ paddingRight: 20 }}>
        <Button text="Cancel" onClick={() => onCancel?.()} />
        <Button
          style={{ marginLeft: 18 }}
          intent={Intent.PRIMARY}
          blue={true}
          text="Download"
          disabled={isProcessing}
          onClick={() => onDownload?.()}
        />
      </section>
    </React.Fragment>
  )
}
